import { Stack } from '@atgfr8/atg-ui-tailwind';
import { SelectInput, ToastType } from '@components';
import { AtgLoader } from '@components/AtgLoader';
import CommonFileUpload, {
  CommonFileUploadFiles,
} from '@components/FileUpload/CommonFileUpload';
import { useParams } from '@solidjs/router';
import {
  applyCustomerDetailsHydration,
  customerDetailsStore,
  CustomerGeneralDocuments,
  deleteCustomerDetailsDocument,
  updateCustomerDefaultLogoUrl,
  updateCustomerDetailsState,
  uploadCustomerDetailsDocument,
} from '@store/customers/customerDetails';
import { ConfigManager } from '@utils/ConfigManager';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast, isFullAdmin } from '@utils/utils';
import { keys, last } from 'lodash';
import { Accessor, Component, createSignal, Show } from 'solid-js';

type CustomerDocumentUploaderProps = {
  documents: Accessor<CustomerGeneralDocuments[]>;
  setDocuments: (documents: CustomerGeneralDocuments[]) => void;
  type: 'Public' | 'Confidential' | 'Misc' | 'Logo';
};

export const CustomerDocumentUploader: Component<
  CustomerDocumentUploaderProps
> = (props) => {
  const [loadingChangeLogo, setLoadingChangeLogo] = createSignal(false);

  const params = useParams();

  const handleFileUpload = async (file: File) => {
    props.setDocuments([
      {
        name: file.name,
        url: '',
      },
      ...props.documents(),
    ]);
    const res = await uploadCustomerDetailsDocument(
      params.id as string,
      file,
      props.type,
    );
    if (res && Boolean(res)) {
      const name =
        typeof res.results[0].url === 'string'
          ? last(res.results[0].url.split('/'))
          : keys(res.results[0].url)[0];

      const tempArr = props.documents().map((doc) => {
        if (doc.name === file.name) {
          return {
            name: name ?? '',
            url: res.results[0].url,
          };
        }
        return doc;
      });
      props.setDocuments(tempArr);
      handleToast(ToastType.Success, 'Customer document uploaded successfully');
    }
  };

  const handleRemoveFile = async (_: number, item: CommonFileUploadFiles) => {
    const urlParams = new URLSearchParams(item.url?.split('?')[1]);
    const payload = {
      file: urlParams.get('url') as string,
      type: 'Customer',
    };
    const res = await deleteCustomerDetailsDocument(payload);
    if (Boolean(res)) {
      const temp = [...props.documents()];
      const index = temp.findIndex((doc) => {
        return (
          doc.url.split('/').includes(props.type) && doc.name === item.name
        );
      });
      temp.splice(index, 1);
      props.setDocuments(temp);
      handleToast(ToastType.Success, 'Customer document deleted successfully');
      if (item.url === customerDetailsStore.customer.defaultLogoUrl) {
        applyCustomerDetailsHydration(() => {
          updateCustomerDetailsState({
            defaultLogoUrl: null,
          });
        });
        await handleDefaultLogoChange('delete');
      }
    }
  };

  const customValidation = (file: File) => {
    const fileExists = props
      .documents()
      .filter((f) => f.url.split('/').includes(props.type))
      .some((f) => {
        const checkLastOccurrence = f.name.lastIndexOf('_');
        const valueBeforeLastOccurrence = f.name.substring(
          0,
          checkLastOccurrence,
        );
        const fileNameWithoutExt = file.name.substring(
          0,
          file.name.lastIndexOf('.'),
        );
        return valueBeforeLastOccurrence === fileNameWithoutExt;
      });

    if (fileExists) {
      handleToast(ToastType.Error, 'File already exists');
      return false;
    }
    return true;
  };

  const handleDefaultLogoChange = async (url: string | 'delete') => {
    setLoadingChangeLogo(true);
    const defaultLogoUrl = url === 'delete' ? null : url;
    const res = await updateCustomerDefaultLogoUrl({
      id: Number(params.id),
      defaultLogoUrl: defaultLogoUrl === 'null' ? null : defaultLogoUrl,
    });
    if (Boolean(res)) {
      handleToast(ToastType.Success, 'Default logo updated successfully');
      applyCustomerDetailsHydration(() => {
        updateCustomerDetailsState({
          defaultLogoUrl: defaultLogoUrl === 'null' ? null : defaultLogoUrl,
        });
      });
    }
    setLoadingChangeLogo(false);
  };

  const getFilteredDoc = () => {
    const { documents, type } = props;
    return documents().filter((doc) => {
      const urlParts = doc.url.split('/');
      if (type === 'Public') {
        return (
          !urlParts.includes('Confidential') &&
          !urlParts.includes('Misc') &&
          !urlParts.includes('Logo')
        );
      }
      return urlParts.includes(type);
    });
  };

  return (
    <Stack spacing={2}>
      <CommonFileUpload
        height={1}
        isMultipleUpload
        maxFileSize={12582912}
        uploadedFiles={getFilteredDoc()}
        acceptedFileTypes={
          props.type === 'Logo' ? ['jpeg', 'jpg', 'png'] : undefined
        }
        shouldUploadOnSelect
        isRemoveFile
        apiCallBackFn={handleFileUpload}
        removeFile={handleRemoveFile}
        customValidation={customValidation}
        isAdmin={isFullAdmin()}
      />
      <Show when={props.type === 'Logo'}>
        <Show when={loadingChangeLogo()}>
          <AtgLoader />
        </Show>
        <SelectInput
          label="Select an Image to Show on BOL"
          menuItems={[
            {
              label: 'Select Image',
              value: 'null',
            },
            ...getFilteredDoc().map((doc) => ({
              label: doc.name.split('_')[1],
              value: doc.url,
            })),
          ]}
          value={customerDetailsStore.customer.defaultLogoUrl ?? 'null'}
          onChange={(val) => void handleDefaultLogoChange(val)}
          dataTestId={formatDataTestId(
            'Customer default BOL logo',
            'selectInput',
          )}
        />
        <Show when={customerDetailsStore.customer.defaultLogoUrl !== null}>
          <img
            class="w-[60%] h-[60%] object-fill border border-gray-300 rounded-xs"
            src={`${ConfigManager.v3Host}${customerDetailsStore.customer.defaultLogoUrl}`}
            alt="default-logo"
          />
        </Show>
      </Show>
    </Stack>
  );
};
